import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { useBack, useWindowWidth } from '../hooks';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, GREEN, BORD, CARD, DARK, MUTED, DIM, TEXT } from '../stockShared';
import { DispatchRows } from './StockSwitches';

// The check when the lorry leaves (the director, 28 Oct 2026; migration 085):
// "when the lorry departs the app checks if everything has been taken off the
// stock and if not, it informs the director with a list and actions to do it
// there and then". Opened from the bell: every line of the lorry -- its goods,
// and its pallets where they are recorded packed -- with Take off now, or Not
// from stock (already taken off another way, or never kept in Stock).

export default function StockDispatch() {
  const { invoiceId } = useParams();
  const goBack = useBack('/stock');
  const { toast } = useToast();
  const phone = useWindowWidth() < 760;
  const [v, setV] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/api/v1/stock/auto/dispatch/${invoiceId}`).then(r => { setV(r.data); setError(null); })
      .catch(err => setError(err.response?.data?.error || 'The check could not be read just now. Check the connection and reload.'));
  }, [invoiceId]);
  useEffect(() => { load(); }, [load]);

  async function take(keys) {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/v1/stock/auto/dispatch/${invoiceId}/take`, { keys });
      setV(data);
      if (data.failed?.length) toast.error(data.failed.join(' '));
      else toast.success(data.clear ? 'Taken off. This lorry is clear.' : 'Taken off.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be taken off just now. Try again.');
    }
    setBusy(false);
  }
  async function skip(key, on) {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/v1/stock/auto/dispatch/${invoiceId}/skip`, { key, skip: on });
      setV(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'It could not be saved just now. Try again.');
    }
    setBusy(false);
  }

  const missing = (v?.rows || []).filter(r => r.state === 'missing' && r.missing > 0);
  const small = (color) => ({ background: 'transparent', color, border: `1px solid ${color}88`, borderRadius: 8, padding: '4px 10px', fontSize: 12.5, fontWeight: 700, cursor: busy ? 'default' : 'pointer', fontFamily: 'system-ui', opacity: busy ? 0.6 : 1 });
  return (
    <div style={{ padding: phone ? '14px 12px 90px' : '20px 24px 60px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: DARK, minHeight: '100vh', boxSizing: 'border-box', maxWidth: 1180, margin: '0 auto' }}>
      <button onClick={goBack} style={{ background: 'none', border: 'none', color: MUTED, fontSize: 12, cursor: 'pointer', padding: 0, marginBottom: 8 }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: 10, marginRight: 4 }} />Back
      </button>
      {error && <div style={{ color: AMBER, fontSize: 13 }}>{error}</div>}
      {!v && !error && <div style={{ color: MUTED, fontSize: 13 }}>Reading…</div>}
      {v && (
        <>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: '0 0 4px' }}>Lorry {v.invoice.number}</h1>
          <div style={{ fontSize: 13, color: DIM, marginBottom: 12 }}>
            {(v.invoice.order || '').split('\n')[0]}{v.invoice.dispatch_date ? ` · left ${new Date(v.invoice.dispatch_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}` : ''}
            {' · '}<Link to="/admin/invoices" style={{ color: TEAL }}>Invoices</Link>
          </div>
          {!v.switches.dispatch.on && (
            <div style={{ background: CARD, border: `1px solid ${AMBER}66`, borderRadius: 10, padding: '10px 12px', fontSize: 13, color: AMBER, marginBottom: 12 }}>
              The check is switched off, so this is only how it stands. Switch it on in <Link to="/stock/switches" style={{ color: TEAL }}>Stock → Automatic stock usage</Link> to take things off here.
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: CARD, border: `1px solid ${v.clear ? `${GREEN}66` : BORD}`, borderRadius: 10, padding: '10px 12px', fontSize: 13.5 }}>
            <span style={{ flex: 1, color: v.clear ? GREEN : TEXT }}>
              {v.clear ? 'Clear: everything on this lorry is taken off stock, or marked not from stock.'
                : `${v.open} thing${v.open === 1 ? '' : 's'} on this lorry ${v.open === 1 ? 'is' : 'are'} not taken off stock yet.`}
            </span>
            {missing.length > 1 && v.switches.dispatch.on && (
              <button type="button" disabled={busy} onClick={() => take(missing.map(r => r.key))}
                style={{ background: TEAL, color: '#0f172a', border: 'none', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'system-ui' }}>
                Take all {missing.length} off now
              </button>
            )}
          </div>
          <DispatchRows rows={v.rows} actions={!v.switches.dispatch.on ? null : (r) => (
            <span style={{ display: 'flex', gap: 6 }}>
              {r.state === 'missing' && r.missing > 0 && <button type="button" disabled={busy} onClick={() => take([r.key])} style={small(TEAL)}>Take {r.what === 'pallets' ? `${r.missing} pallet${r.missing === 1 ? '' : 's'}` : r.missing} off now</button>}
              {(r.state === 'missing' || r.state === 'unknown' || r.state === 'unit') && <button type="button" disabled={busy} onClick={() => skip(r.key, true)} style={small(DIM)}>Not from stock</button>}
              {r.state === 'not_from_stock' && <button type="button" disabled={busy} onClick={() => skip(r.key, false)} style={small(DIM)}>Count it again</button>}
            </span>
          )} />
          <p style={{ color: MUTED, fontSize: 12.5, marginTop: 10, lineHeight: 1.5 }}>
            Take off now: the goods leave stock as gone out on this lorry; missing pallets are recorded packed for it, their pallet, film, cover sheet, tape and label coming off. Not from stock: already taken off another way, or never kept in Stock -- nothing changes.
          </p>
        </>
      )}
    </div>
  );
}
